import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, TextField, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, Tooltip, CircularProgress, LinearProgress,
  Stack, Select, MenuItem, FormControl, InputLabel, Collapse, Alert,
  Divider,
} from '@mui/material';
import {
  KeyboardArrowDown, KeyboardArrowRight, Search, Refresh,
  CheckCircleOutline, ErrorOutline, WarningAmber,
} from '@mui/icons-material';
import apiService from '../../services/api';
import { createLogger, adminLogContext } from '../../utils/logger';

const logger = createLogger('PipelineTraceViewer');
const ctx = (extra) => adminLogContext('pipeline_trace', extra);

const STAGE_ORDER = [
  'chunking', 'pass_1', 'toc_compression', 'pass_2', 'pass_3', 'pass_4',
  'bucketing', 'synthesis', 'format_to_db', 'enrichment',
  'site_plan_selection', 'save',
];

const STAGE_LABELS = {
  chunking: 'Chunking',
  pass_1: 'Pass 1 — Index & Classify',
  toc_compression: 'TOC Compression',
  pass_2: 'Pass 2 — Confirm Cases',
  pass_3: 'Pass 3 — Mini Reports',
  pass_4: 'Pass 4 — Classification',
  bucketing: 'Bucketing',
  synthesis: 'Synthesis',
  format_to_db: 'Format → DB',
  enrichment: 'Enrichment',
  site_plan_selection: 'Site Plan Selection',
  save: 'Save to DB',
};

const statusIcon = (status) => {
  if (status === 'success') return <CheckCircleOutline sx={{ fontSize: 16, color: 'success.main' }} />;
  if (status === 'truncated') return <WarningAmber sx={{ fontSize: 16, color: 'warning.main' }} />;
  if (status === 'failed') return <ErrorOutline sx={{ fontSize: 16, color: 'error.main' }} />;
  return null;
};

const formatTokens = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x) || x <= 0) return '';
  if (x >= 1_000_000) return `${(x / 1_000_000).toFixed(2)}M`;
  if (x >= 1_000) return `${(x / 1_000).toFixed(1)}k`;
  return String(Math.trunc(x));
};

const formatUsd = (usd) => {
  const x = Number(usd);
  if (!Number.isFinite(x) || x <= 0) return '';
  if (x < 0.01) return `$${x.toFixed(4)}`;
  return `$${x.toFixed(2)}`;
};

const truncate = (s, max = 120) => {
  if (!s || typeof s !== 'string') return '';
  return s.length > max ? s.slice(0, max) + '…' : s;
};

// ── Expandable item detail (prompt, response, thinking, data) ──
const ItemDetail = ({ item }) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [showResponse, setShowResponse] = useState(false);
  const [showThinking, setShowThinking] = useState(false);

  const Pre = ({ children }) => (
    <Box component="pre" sx={{
      m: 0, p: 1.5, mt: 0.5, bgcolor: 'action.hover', borderRadius: 1,
      fontSize: 11, maxHeight: 300, overflow: 'auto', whiteSpace: 'pre-wrap',
      wordBreak: 'break-word', fontFamily: 'monospace',
    }}>{children}</Box>
  );

  return (
    <Box sx={{ p: 2, bgcolor: 'background.default', borderTop: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" spacing={3} sx={{ mb: 1 }}>
        {item.input_data && (
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Input Data</Typography>
            <Pre>{JSON.stringify(item.input_data, null, 2)}</Pre>
          </Box>
        )}
        {item.output_data && (
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Output Data</Typography>
            <Pre>{JSON.stringify(item.output_data, null, 2)}</Pre>
          </Box>
        )}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        {item.prompt_text && (
          <Chip label={showPrompt ? 'Hide Prompt' : 'Show Prompt'} size="small" variant="outlined"
            onClick={() => setShowPrompt(!showPrompt)} />
        )}
        {item.response_text && (
          <Chip label={showResponse ? 'Hide Response' : 'Show Response'} size="small" variant="outlined"
            onClick={() => setShowResponse(!showResponse)} />
        )}
        {item.thinking_content && (
          <Chip label={showThinking ? 'Hide Thinking' : 'Show Thinking'} size="small" variant="outlined"
            color="info" onClick={() => setShowThinking(!showThinking)} />
        )}
      </Stack>

      {showPrompt && item.prompt_text && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Prompt ({item.prompt_text.length.toLocaleString()} chars)</Typography>
          <Pre>{item.prompt_text}</Pre>
        </Box>
      )}
      {showResponse && item.response_text && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Response ({item.response_text.length.toLocaleString()} chars)</Typography>
          <Pre>{item.response_text}</Pre>
        </Box>
      )}
      {showThinking && item.thinking_content && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="info.main" sx={{ fontWeight: 600 }}>Thinking</Typography>
          <Pre>{item.thinking_content}</Pre>
        </Box>
      )}

      {item.error_message && (
        <Alert severity="error" sx={{ mt: 1, fontSize: 12 }}>{item.error_message}</Alert>
      )}
    </Box>
  );
};

// ── Single item row ──
const ItemRow = ({ item }) => {
  const [open, setOpen] = useState(false);
  const hasDetail = item.prompt_text || item.response_text || item.input_data || item.output_data || item.thinking_content;

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: hasDetail ? 'pointer' : 'default', '& > *': { borderBottom: open ? 0 : undefined } }}
        onClick={() => hasDetail && setOpen(!open)}
      >
        <TableCell sx={{ width: 28, p: 0.5 }}>
          {hasDetail && (
            <IconButton size="small" sx={{ p: 0.25 }}>
              {open ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ fontSize: 12 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {statusIcon(item.status)}
            <span>{STAGE_LABELS[item.stage] || item.stage}</span>
          </Stack>
        </TableCell>
        <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>{item.item_key}</TableCell>
        <TableCell sx={{ fontSize: 12 }} align="right">{formatTokens(item.input_tokens)}</TableCell>
        <TableCell sx={{ fontSize: 12 }} align="right">{formatTokens(item.output_tokens)}</TableCell>
        <TableCell sx={{ fontSize: 12 }} align="right">{formatTokens(item.thinking_tokens)}</TableCell>
        <TableCell sx={{ fontSize: 12 }} align="right">{formatUsd(item.cost_usd)}</TableCell>
        <TableCell sx={{ fontSize: 12 }}>{item.finish_reason || ''}</TableCell>
        <TableCell sx={{ fontSize: 12 }} align="right">{item.attempts > 1 ? item.attempts : ''}</TableCell>
      </TableRow>
      {hasDetail && (
        <TableRow>
          <TableCell colSpan={9} sx={{ p: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <ItemDetail item={item} />
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

// ── Trace panel (loaded when a report entry is selected) ──
const TracePanel = ({ entryId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    const t0 = performance.now();
    apiService.getReportTrace(entryId)
      .then((d) => {
        if (!cancelled) {
          setData(d);
          logger.info('Report trace loaded', ctx({
            action: 'load_report_trace',
            entry_id: String(entryId),
            step_count: (d?.items || []).length,
            load_time_ms: Math.round(performance.now() - t0),
          }));
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e.message || 'Failed to load trace');
          logger.apiFailure('Failed to load report trace', ctx({
            action: 'load_report_trace',
            entry_id: String(entryId),
          }), e);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [entryId]);

  if (loading && !data) return <Box sx={{ p: 3 }}><LinearProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  if (!data) return null;

  const items = data.items || [];

  // Sort by stage order, then created_at
  const sorted = [...items].sort((a, b) => {
    const ai = STAGE_ORDER.indexOf(a.stage);
    const bi = STAGE_ORDER.indexOf(b.stage);
    if (ai !== bi) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    return (a.created_at || '').localeCompare(b.created_at || '');
  });

  const totalCost = sorted.reduce((s, i) => s + Number(i.cost_usd || 0), 0);

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2">
          {data.entry?.case_number || entryId}
        </Typography>
        <Chip label={`${sorted.length} steps`} size="small" variant="outlined" />
        {totalCost > 0 && <Chip label={formatUsd(totalCost)} size="small" color="primary" variant="outlined" />}
        {data.entry?.last_task_id && (
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            task: {data.entry.last_task_id.slice(0, 8)}…
          </Typography>
        )}
      </Stack>

      {sorted.length === 0 ? (
        <Alert severity="info">No pipeline trace data for this report yet.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 28 }} />
                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Stage</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Item</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12 }} align="right">In</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12 }} align="right">Out</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12 }} align="right">Think</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12 }} align="right">Cost</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Finish</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12 }} align="right">Tries</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((item, i) => <ItemRow key={item.id || i} item={item} />)}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// ── Main component ──
export default function PipelineTraceViewer() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [counties, setCounties] = useState([]);
  const [selectedCounty, setSelectedCounty] = useState('');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [offset, setOffset] = useState(0);
  const limit = 25;

  // Load counties for filter
  useEffect(() => {
    apiService.getAllCounties()
      .then((d) => setCounties(d?.counties || []))
      .catch((e) => {
        logger.apiFailure('Failed to load counties for trace filter', ctx({ action: 'load_counties' }), e);
      });
  }, []);

  const loadEntries = useCallback(() => {
    const t0 = performance.now();
    setLoading(true);
    apiService.getReportEntries({ search, countyId: selectedCounty, limit, offset })
      .then((d) => {
        setEntries(d?.entries || []);
        setTotal(d?.total || 0);
        logger.info('Report entries loaded', ctx({
          action: 'load_report_entries',
          result_count: (d?.entries || []).length,
          total: d?.total || 0,
          county_id: selectedCounty ? String(selectedCounty) : undefined,
          search: search || undefined,
          load_time_ms: Math.round(performance.now() - t0),
        }));
      })
      .catch((e) => {
        setEntries([]);
        logger.apiFailure('Failed to load report entries', ctx({
          action: 'load_report_entries',
          county_id: selectedCounty ? String(selectedCounty) : undefined,
          search: search || undefined,
        }), e);
      })
      .finally(() => setLoading(false));
  }, [search, selectedCounty, offset]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setOffset(0);
    setSelectedEntry(null);
  };

  return (
    <Box sx={{ pt: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          size="small" placeholder="Search case number…" value={search}
          onChange={handleSearch}
          InputProps={{ startAdornment: <Search sx={{ mr: 0.5, color: 'text.disabled', fontSize: 18 }} /> }}
          sx={{ width: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>County</InputLabel>
          <Select label="County" value={selectedCounty}
            onChange={(e) => { setSelectedCounty(e.target.value); setOffset(0); setSelectedEntry(null); }}>
            <MenuItem value="">All counties</MenuItem>
            {counties.map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.name}, {c.state}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <IconButton size="small" onClick={loadEntries}><Refresh fontSize="small" /></IconButton>
        <Typography variant="caption" color="text.secondary">
          {total > 0 ? `${offset + 1}–${Math.min(offset + limit, total)} of ${total}` : 'No results'}
        </Typography>
        {offset > 0 && (
          <Chip label="← Prev" size="small" variant="outlined" clickable
            onClick={() => setOffset(Math.max(0, offset - limit))} />
        )}
        {offset + limit < total && (
          <Chip label="Next →" size="small" variant="outlined" clickable
            onClick={() => setOffset(offset + limit)} />
        )}
      </Stack>

      <Stack direction="row" spacing={2} sx={{ minHeight: 400 }}>
        {/* Left panel: entry list */}
        <TableContainer component={Paper} variant="outlined" sx={{ flex: '0 0 480px', maxHeight: 600, overflow: 'auto' }}>
          {loading && <LinearProgress />}
          <Table size="small" stickyHeader sx={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Case #</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Categories</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Trace</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((e) => (
                  <TableRow key={e.id} hover
                    selected={selectedEntry === e.id}
                    onClick={() => setSelectedEntry(e.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 500 }}>
                      {e.case_number || '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: 11 }}>
                      {(e.category_names || []).join(', ')}
                    </TableCell>
                    <TableCell sx={{ fontSize: 11 }}>{e.current_status || ''}</TableCell>
                    <TableCell sx={{ fontSize: 11 }}>
                      {e.last_task_id ? (
                        <Chip label="Has trace" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                      ) : (
                        <Chip label="No trace" size="small" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {entries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      No report entries found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
        </TableContainer>

        {/* Right panel: trace detail */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {selectedEntry ? (
            <TracePanel key={selectedEntry} entryId={selectedEntry} />
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">Select a report entry to view its pipeline trace.</Typography>
            </Box>
          )}
        </Box>
      </Stack>
    </Box>
  );
}